import { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../components/utils/PMDialog";
import { resolveLanguage } from "../i18n/languages";

/**
 * "Ny funksjon" dialog (design: PROMANG_COMPONENTS.md §5 dialog shell with
 * eyebrow/subtitle header, `.pm-surface-card`, `.pm-alert`, `.pm-form-footer`).
 * Presentational: the host owns the queue, read receipts and language change.
 */
export function FeatureAnnouncementDialog({
  announcement,
  position = 1,
  total = 1,
  currentLanguage,
  onDismiss,
  onConfirm,
  onSwitchLanguage,
}) {
  const { t, i18n } = useTranslation();
  const [switching, setSwitching] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  if (!announcement) return null;

  const eyebrow =
    total > 1
      ? t("featureAnnouncements.eyebrow_counter", { current: position, total })
      : t("featureAnnouncements.eyebrow");

  if (announcement.type === "language") {
    const language = announcement.language;
    const current = resolveLanguage(currentLanguage);
    // The new language's own words, so someone who reads only that language understands the offer.
    const tNew = i18n.getFixedT(language.code);
    const switchLanguage = async () => {
      setSwitching(true);
      setSwitchFailed(false);
      try {
        await onSwitchLanguage(announcement);
      } catch {
        setSwitchFailed(true);
        setSwitching(false);
      }
    };
    return (
      <PMDialog
        open
        size="xl"
        eyebrow={eyebrow}
        title={t("featureAnnouncements.language.title", { language: language.nativeName })}
        subtitle={t("featureAnnouncements.language.subtitle")}
        onClose={switching ? () => {} : () => onDismiss(announcement)}
      >
        <div className="space-y-4">
          <div className="pm-surface-card flex items-start gap-3 p-4" lang={language.code}>
            <LanguageIcon className="mt-0.5 h-6 w-6 shrink-0" style={{ color: "var(--pls-accent)" }} aria-hidden="true" />
            <div className="min-w-0">
              <p className="pm-fact__label">{language.nativeName}</p>
              <p className="pm-fact__value">{tNew("featureAnnouncements.language.subtitle")}</p>
            </div>
          </div>
          <p className="pm-help">{t("featureAnnouncements.language.hint")}</p>
          <div className="pm-form-footer mt-3 shrink-0 rounded-xl">
            {switchFailed && (
              <p role="alert" className="pm-form-footer__summary">
                <span className="pm-form-footer__summary-icon" aria-hidden="true">!</span>
                <span>{t("featureAnnouncements.language.switch_failed")}</span>
              </p>
            )}
            <span className="flex-1" />
            <button
              type="button"
              disabled={switching}
              className="pm-btn pm-btn--secondary"
              onClick={() => onConfirm(announcement, "kept_language")}
            >
              {t("featureAnnouncements.language.keep", { language: current.nativeName })}
            </button>
            <button
              type="button"
              disabled={switching}
              aria-busy={switching || undefined}
              className="pm-btn pm-btn--primary"
              onClick={switchLanguage}
            >
              {switching && <span className="pm-btn__spinner" aria-hidden="true" />}
              {tNew("featureAnnouncements.language.switch", { language: language.nativeName })}
            </button>
          </div>
        </div>
      </PMDialog>
    );
  }

  const highlights = Array.isArray(announcement.highlightKeys) ? announcement.highlightKeys : [];
  return (
    <PMDialog
      open
      size="xl"
      eyebrow={eyebrow}
      title={t(announcement.titleKey)}
      subtitle={announcement.subtitleKey ? t(announcement.subtitleKey) : undefined}
      onClose={() => onDismiss(announcement)}
    >
      <div className="space-y-4">
        {announcement.bodyKey && <p className="text-sm">{t(announcement.bodyKey)}</p>}
        {highlights.length > 0 && (
          <ul className="pm-surface-card space-y-2 p-4">
            {highlights.map((key) => (
              <li key={key} className="flex items-start gap-2 text-sm">
                <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--pls-accent)" }} aria-hidden="true" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="pm-form-footer mt-3 shrink-0 rounded-xl">
          <span className="flex-1" />
          {announcement.learnMorePath && (
            <Link
              to={announcement.learnMorePath}
              className="pm-btn pm-btn--secondary"
              onClick={() => onConfirm(announcement, "confirmed")}
            >
              {t("featureAnnouncements.learn_more")}
            </Link>
          )}
          <button type="button" className="pm-btn pm-btn--primary" onClick={() => onConfirm(announcement, "confirmed")}>
            {t("featureAnnouncements.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

FeatureAnnouncementDialog.propTypes = {
  announcement: PropTypes.object,
  position: PropTypes.number,
  total: PropTypes.number,
  currentLanguage: PropTypes.string,
  onDismiss: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSwitchLanguage: PropTypes.func,
};
